import { PaymentModuleId } from '@foundation/apis/payments';
import { SupportedLanguage } from '@foundation/localization';
import Clarity from '@microsoft/clarity';
import { useTranslation } from '@shared/localization';
import { useMemo, JSX } from 'react';

import { disableUIBlocker, enableUIBlocker } from '@/components/common';
import { RedirectStatus } from '@/components/contexts/params';
import { useUserFriendlyPaymentModuleName } from '@/hooks/useUserFriendlyPaymentModuleName';
import { triggerErrorToast, triggerInfoToast } from '@/libs/toasts';
import { ApplePaySupportedLocale } from '@/services/braintree';
import BraintreeApplePayService from '@/services/braintree/BraintreeApplePayService';
import Logger, { LoggerOrigin } from '@/services/Logger';
import NavigationService, {
  InternalPage,
} from '@/services/navigation/NavigationService';
import TelemetryService from '@/services/telemetry/TelemetryService';
import { getQaTestingProps, QaTestingElements } from '@/utils/qaTesting';

interface IPayWithBraintreeApplePayProps {
  navigation?: JSX.Element | null;
}

export function PayWithBraintreeApplePay({
  navigation,
}: IPayWithBraintreeApplePayProps) {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const paymentModuleT = useUserFriendlyPaymentModuleName();

  const applePayLocale = useMemo(
    () => ApplePaySupportedLocale[language as SupportedLanguage],
    [language],
  );

  return (
    // The outer wrapper is hack to control the height of the perceived button
    <div className="flex flex-row space-x-2">
      {navigation}
      <div
        className="apple-pay-btn-container w-full"
        {...getQaTestingProps(QaTestingElements.ApplyPayButton)}
      >
        <button
          // All language values in BraintreeApplePayLocale are valid lang values.
          // However, the rule axe/language throw an error when using expressions
          // since it cannot confirm the strings we assign. This hack avoids
          // flagging this as an error.
          {...{ lang: applePayLocale }}
          onClick={() => {
            enableUIBlocker();
            const currentPage = NavigationService.getInternalPage();
            NavigationService.navigateTo({
              page: InternalPage.StatusProcessing,
            });
            BraintreeApplePayService.authorize()
              .then(() => {
                NavigationService.navigateTo({
                  page: InternalPage.StatusSuccess,
                });
                Clarity.setTag('pay', [
                  RedirectStatus.Success,
                  paymentModuleT(PaymentModuleId.BraintreeApplePay),
                ]);
              })
              .catch((err: Error | undefined) => {
                NavigationService.navigateTo(currentPage);

                if (err) {
                  // Failed to authorize
                  TelemetryService.trackException(err, 'BraintreeApplePayBtn');
                  Logger.error(
                    LoggerOrigin.ReactComponent,
                    'ApplePay authorization failed',
                    err,
                  );
                  Clarity.setTag('pay', [
                    RedirectStatus.Failure,
                    paymentModuleT(PaymentModuleId.BraintreeApplePay),
                  ]);
                  triggerErrorToast(
                    t('Something went wrong!'),
                    t(
                      'Operation failed. Please try again with a different payment method.',
                    ),
                  );
                } else {
                  // Payment sheet dismissed/operation cancelled
                  Logger.log(
                    LoggerOrigin.ReactComponent,
                    'ApplePay session aborted',
                  );
                  Clarity.setTag('pay', [
                    RedirectStatus.Canceled,
                    paymentModuleT(PaymentModuleId.BraintreeApplePay),
                  ]);
                  triggerInfoToast(
                    t('Select a new payment method and proceed to pay.'),
                  );
                }
              })
              .finally(() => disableUIBlocker());
          }}
          type="button"
        >
          {''}
        </button>
      </div>
    </div>
  );
}
